import { Flex, Segmented, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDate, orDash } from '../../lib/format';
import { FINANCING_STATUS_LABEL, SLOW_DECISION_DAYS, financingTone, isSlow } from './financing-status';
import { listFinancing, type FinancingApplication } from './financing-api';
import { formatMoney } from '../../lib/money';

const { Text } = Typography;

/**
 * "Which loans have gone quiet" is a question a sales manager asks every
 * morning, and it is the only reason this list exists as its own screen — an
 * advisor works one application at a time, from the booking.
 */
type View = 'slow' | 'withBank' | 'collecting' | 'all';

export function FinancingListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('slow');

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['financing', 'list', view, search], (page) =>
    listFinancing({
      ...page,
      q: search,
      ...(view === 'slow' ? { awaitingLongerThanDays: SLOW_DECISION_DAYS } : {}),
      ...(view === 'withBank' ? { status: 'SUBMITTED' as const } : {}),
      ...(view === 'collecting' ? { status: 'PENDING' as const } : {}),
    }),
  );

  const emptyFor: Record<View, string> = {
    slow: t('financing.emptySlow', { days: SLOW_DECISION_DAYS }),
    withBank: t('financing.emptyWithBank'),
    collecting: t('financing.emptyCollecting'),
    all: t('financing.emptyAll'),
  };

  return (
    <Flex vertical gap={12}>
      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'slow', label: t('financing.viewSlow') },
          { value: 'withBank', label: t('financing.viewWithBank') },
          { value: 'collecting', label: t('financing.viewCollecting') },
          { value: 'all', label: t('financing.viewAll') },
        ]}
      />

      <ListPage<FinancingApplication>
        title={t('financing.title')}
        searchPlaceholder={t('financing.search')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={search.length > 0 ? t('financing.noResults') : emptyFor[view]}
        columns={[
          { title: t('sales.fields.customer'), dataIndex: 'customerName' },
          { title: t('financing.fields.provider'), dataIndex: 'providerName', width: 190 },
          {
            title: t('sales.fields.status'),
            dataIndex: 'status',
            width: 170,
            render: (status: FinancingApplication['status']) => (
              <Tag color={financingTone(status)}>{FINANCING_STATUS_LABEL[status]}</Tag>
            ),
          },
          {
            title: t('financing.fields.waiting'),
            dataIndex: 'daysAwaitingDecision',
            width: 130,
            align: 'right' as const,
            render: (days: number | null) =>
              days === null ? (
                '—'
              ) : (
                <Text type={isSlow(days) ? 'danger' : undefined} strong={isSlow(days)}>
                  {t('financing.days', { count: days })}
                </Text>
              ),
          },
          {
            title: t('financing.fields.amount'),
            dataIndex: 'amount',
            width: 150,
            align: 'right' as const,
            render: (value: string, row: FinancingApplication) => formatMoney(value, row.currency),
          },
          {
            title: t('financing.fields.providerRefNo'),
            dataIndex: 'providerRefNo',
            width: 180,
            render: (value: string | null) => orDash(value),
          },
          {
            title: t('financing.fields.submittedAt'),
            dataIndex: 'submittedAt',
            width: 140,
            render: (value: string | null) => (value === null ? '—' : formatDate(value)),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        // The application is a tab on the booking, which is where it is acted
        // on. Sending the manager anywhere else would be sending them to a
        // screen with no context and no controls.
        onRowClick={(row) => {
          void navigate(`/bookings/${row.bookingUid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong>{row.customerName}</Text>
              <Tag color={financingTone(row.status)} style={{ marginInlineEnd: 0 }}>
                {FINANCING_STATUS_LABEL[row.status]}
              </Tag>
            </Flex>
            <Flex justify="space-between" gap={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.providerName}
              </Text>
              {row.daysAwaitingDecision !== null && (
                <Text type={isSlow(row.daysAwaitingDecision) ? 'danger' : 'secondary'}>
                  {t('financing.days', { count: row.daysAwaitingDecision })}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
